import React, { memo, useEffect, useMemo, useState, useCallback } from "react";
import { Button, ButtonGroup } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { DateTime } from "luxon";
import { InputDatePicker, InputDropdown } from "../Form";
import { Col, Row } from "react-bootstrap";

const StandardConst = {
  timeFrame: {
    Week: "Week",
    Month: "Month",
    SemiMonth: "SemiMonth",
    SixMonth: "SixMonth",
    Year: "Year",
    Custom: "Custom",
  },
};

const getDateRange = (type, referenceDate = DateTime.local()) => {
  let startDate, endDate;

  switch (type) {
    case StandardConst.timeFrame.Week:
      startDate = referenceDate.startOf("week");
      endDate = referenceDate.endOf("week");
      break;
    case StandardConst.timeFrame.SemiMonth:
      if (referenceDate.day <= 15) {
        startDate = referenceDate.set({ day: 1 });
        endDate = referenceDate.set({ day: 15 });
      } else {
        startDate = referenceDate.set({ day: 16 });
        endDate = referenceDate.endOf("month");
      }
      break;
    case StandardConst.timeFrame.Month:
      startDate = referenceDate.startOf("month");
      endDate = referenceDate.endOf("month");
      break;
    case StandardConst.timeFrame.SixMonth:
      startDate = referenceDate.minus({ months: 5 }).startOf("month");
      endDate = referenceDate.endOf("month");
      break;
    case StandardConst.timeFrame.Year:
      startDate = referenceDate.startOf("year");
      endDate = referenceDate.endOf("year");
      break;
    default:
      return null;
  }

  return {
    FromDate: startDate.toFormat("yyyy-MM-dd"),
    ToDate: endDate.toFormat("yyyy-MM-dd"),
  };
};

const FilterableViewComponent = ({ filter, setFilter }) => {
  const [timeFrame, setTimeFrame] = useState(StandardConst.timeFrame.Week);

  // Apply initial range on mount or timeFrame change
  useEffect(() => {
    if (timeFrame !== StandardConst.timeFrame.Custom) {
      const range = getDateRange(timeFrame);
      if (range) setFilter((prev) => ({ ...prev, ...range }));
    }
  }, [timeFrame, setFilter]);

  const dropdownOptions = useMemo(
    () =>
      Object.values(StandardConst.timeFrame).map((text) => ({
        value: text,
        text,
      })),
    []
  );

  const shiftDateRange = useCallback(
    (direction) => {
      const currentFrom = DateTime.fromISO(filter.FromDate);
      let newFrom, newTo;

      switch (timeFrame) {
        case StandardConst.timeFrame.Week:
          newFrom = currentFrom.plus({ weeks: direction });
          newTo = newFrom.endOf("week");
          break;
        case StandardConst.timeFrame.SemiMonth:
          if (currentFrom.day <= 15) {
            newFrom = currentFrom.set({ day: 16 }).plus({ months: direction > 0 ? 0 : -1 });
            newTo = newFrom.endOf("month");
          } else {
            newFrom = currentFrom.set({ day: 1 }).plus({ months: direction });
            newTo = newFrom.set({ day: 15 });
          }
          break;
        case StandardConst.timeFrame.Month:
          newFrom = currentFrom.plus({ months: direction }).startOf("month");
          newTo = newFrom.endOf("month");
          break;
        case StandardConst.timeFrame.SixMonth:
          newFrom = currentFrom.plus({ months: 6 * direction }).startOf("month");
          newTo = newFrom.plus({ months: 5 }).endOf("month");
          break;
        case StandardConst.timeFrame.Year:
          newFrom = currentFrom.plus({ years: direction }).startOf("year");
          newTo = newFrom.endOf("year");
          break;
        default:
          return;
      }

      setFilter((prev) => ({
        ...prev,
        FromDate: newFrom.toFormat("yyyy-MM-dd"),
        ToDate: newTo.toFormat("yyyy-MM-dd"),
      }));
    },
    [filter.FromDate, setFilter, timeFrame]
  );

  const handleTimeFrameChange = useCallback(
    (selectedFrame) => {
      setTimeFrame(selectedFrame);
      if (selectedFrame !== StandardConst.timeFrame.Custom) {
        const range = getDateRange(selectedFrame);
        if (range) setFilter((prev) => ({ ...prev, ...range }));
      }
    },
    [setFilter]
  );

  return (
    <span className="d-flex flex-row flex-md-nowrap bg-primary gap-6 ">
        {timeFrame !== StandardConst.timeFrame.Custom && (
          <ButtonGroup size="small" variant="outlined" className="">
            <Button onClick={() => shiftDateRange(-1)}>
              <ArrowBack className="text-white" />
            </Button>
            <Button onClick={() => shiftDateRange(1)}>
              <ArrowForward className="text-white" />
            </Button>
          </ButtonGroup>
        )}
        <span className="mx-2 h4 text-white">
          <strong>This {timeFrame}: </strong>
          {`${DateTime.fromISO(filter.FromDate).toFormat("dd MMM")} - ${DateTime.fromISO(
            filter.ToDate
          ).toFormat("dd MMM yyyy")}`}
        </span>
    
  
        <div className="d-flex gap-2 align-items-center">
          <div style={{ minWidth: '180px' }}>
            <InputDropdown setValue={handleTimeFrameChange} ddOpt={dropdownOptions} className='bg-primary text-white '  />
          </div>
          {timeFrame === StandardConst.timeFrame.Custom && (
            <>
              <div style={{ minWidth: '180px' }}>
                <InputDatePicker
                  labelCss="text-center"
                  value={filter.FromDate}
                  setValue={(v) => setFilter((prev) => ({ ...prev, FromDate: v }))}
                  max={filter.ToDate || undefined}
                />
              </div>
              <div style={{ minWidth: '180px' }}>
                <InputDatePicker
                  labelCss="text-center"
                  value={filter.ToDate}
                  setValue={(v) => setFilter((prev) => ({ ...prev, ToDate: v }))}
                  min={filter.FromDate || undefined}
                />
              </div>
            </>
          )}
        </div>
  
  </span>
  
  );
};

export default memo(FilterableViewComponent);
